import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useRobots, useTasks, useAlerts, useAlertSummary } from '../api/hooks';
import { useIssueSummary } from '../api/hooks/useIssues';
import { useActiveSite } from '../state/useActiveSite';
import { useAuth, useHighestRole } from '../auth/useAuth';
import { scopeByActiveSite } from '../api/scope';
import { visibleIssueSources } from '../screens/Support/issue-visibility';
import { useLifts } from '../api/hooks/useLifts';
import { useFeedHealth } from '../api/feed-health';
import { useCanSeeSimulated } from '../auth/useCanSeeSimulated';
import { useRobotPresence } from '../screens/Map/useRobotPresence';
import { robotsOnline } from './robots-online';
import { IconBell, IconBot, IconClipboardList, IconLifeBuoy } from '../design-system/icons';
import type { RoleToken } from '../auth/types';
import './TopBarStats.css';

/** Matches `SideNav.NAV_ENTRIES`' gating for Alerts/Support. */
const OPERATOR_ROLES: ReadonlyArray<RoleToken> = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:service_operator',
];

function StatPill({
  to,
  icon,
  value,
  label,
  ariaLabel,
  loading,
  tone = 'default',
  title,
}: {
  to: string;
  icon: ReactNode;
  /** `null` = the count could not be read: shown as "?", never as 0. */
  value: number | null;
  label: string;
  ariaLabel: string;
  loading: boolean;
  tone?: 'default' | 'warning' | 'error';
  /** Native tooltip, for the one-line "what exactly is counted here". */
  title?: string;
}) {
  const spoken = loading ? 'loading' : value === null ? "can't check" : value;
  const shown = loading ? '—' : value === null ? '?' : value;
  return (
    <Link
      to={to}
      className={`nx-topbar-stat nx-topbar-stat--${tone}`}
      aria-label={`${ariaLabel}: ${spoken}. Open ${ariaLabel}.`}
      // The words are in the tooltip as well: where the bar has no room
      // for them the chip is icon + number only.
      title={title ? `${ariaLabel}. ${title}` : ariaLabel}
      data-testid={`topbar-stat-${to.replace(/^\//, '')}`}
    >
      <span className="nx-topbar-stat__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="nx-topbar-stat__value">{shown}</span>
      <span className="nx-topbar-stat__label">{label}</span>
    </Link>
  );
}

/**
 * Always-visible Robots/Tasks/Alerts/Issues counters — moved here
 * from a card row at the top of `OverviewScreen` (UI quick-actions
 * request) so the same at-a-glance status is available from every
 * screen, not just Overview, and Overview's map can use the freed
 * vertical space. Same data sources and role gating as the cards
 * they replaced (Alerts/Issues stay operator-role-only, matching
 * `SideNav`'s own gating for those two screens).
 */
/**
 * Whether the chips' words fit: they go (icon + number stay) when the
 * labelled chips are wider than the room the bar gives the cluster, and
 * come back once that room is at least the labelled width again. Measured
 * rather than set by a breakpoint, because the room depends on the side
 * menu (labels or icons), the signed-in name and the counts' digits --
 * a fixed cutoff clipped the first chip mid-word ("INE") at 1280-1420px
 * with the full menu.
 */
function useWordsFit(deps: readonly unknown[]) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [wordless, setWordless] = useState(false);
  const labelledW = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (el.clientWidth <= 1) return; // not laid out: tucked away, or jsdom
      if (el.dataset.words !== 'off') {
        if (el.scrollWidth > el.clientWidth + 1) {
          labelledW.current = el.scrollWidth;
          setWordless(true);
        }
      } else if (labelledW.current > 0 && el.clientWidth >= labelledW.current) {
        setWordless(false);
      }
    };
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    for (const chip of Array.from(el.children)) ro.observe(chip);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { ref, wordless };
}

export function TopBarStats() {
  const { site } = useActiveSite();
  const role = useHighestRole();
  const { roles } = useAuth();
  const robots = useRobots();
  const tasks = useTasks();
  // Still mounted even though the badge no longer reads it: this is
  // the always-on poll that fills the `alertsKey` cache the bell/toast
  // provider subscribes to. Dropping it would silence the bell on
  // every screen except Alerts.
  useAlerts();
  // Badge values come from the server-side `count(*)` summaries, NOT
  // from `.length` of the list responses — those are capped pages, so
  // their length saturates at the page limit (the Issues badge sat
  // pinned at exactly 200) and the issues list counted resolved rows
  // as if they were open.
  const alertSummary = useAlertSummary();
  // Count the same source set the viewer's Support screen shows
  // (ordinary operators are pinned to `task` there); the summary
  // endpoint excludes `device` rows server-side regardless — those
  // are Alert rows, already counted by the Alerts badge.
  const issueSummary = useIssueSummary(visibleIssueSources(roles));

  const scopedRobots = useMemo(
    () => scopeByActiveSite(robots.data ?? [], site?.id ?? ''),
    [robots.data, site?.id],
  );
  // Online is counted by presence, not by the last status a record
  // carried (`robots-online.ts`): "?" while the feed is clamped or a robot
  // is still being checked, and never the simulated fleet for the hotel.
  const lifts = useLifts().data;
  const presence = useRobotPresence(scopedRobots, lifts);
  const feed = useFeedHealth(site?.id ?? '');
  const showSimulated = useCanSeeSimulated();
  const scopedTasks = useMemo(
    () => scopeByActiveSite(tasks.data ?? [], site?.id ?? ''),
    [tasks.data, site?.id],
  );

  const canSee = role !== null && OPERATOR_ROLES.includes(role);
  const fit = useWordsFit([site?.id, canSee]);

  if (!site) return null;

  const activeRobots = robotsOnline(scopedRobots, presence, { clamped: feed.clamped, showSimulated });
  const activeTasks = scopedTasks.filter((t) => t.status === 'active' || t.status === 'queued').length;
  // "?" when the summary read failed, even with an older count cached:
  // a number the server can no longer vouch for is not a count, and 0 in
  // particular reads as "nothing open".
  const openAlerts = alertSummary.isError ? null : (alertSummary.data?.open ?? 0);
  // Open + in-progress: an issue someone has picked up is still an
  // unresolved problem from the operator's seat; only `resolved`
  // leaves the badge.
  const unresolvedIssues =
    (issueSummary.data?.open ?? 0) + (issueSummary.data?.in_progress ?? 0);
  const canSeeOperatorStats = canSee;

  return (
    <div
      ref={fit.ref}
      className="nx-topbar-stats"
      data-testid="topbar-stats"
      data-tuck="hide"
      data-words={fit.wordless ? 'off' : 'on'}
    >
      <StatPill
        to="/fleet"
        icon={<IconBot size={14} />}
        value={activeRobots}
        label="Online"
        ariaLabel="Robots online"
        title="Robots heard from in the last 30 seconds. Shows ? while this page is reconnecting or still checking a robot."
        loading={robots.isLoading}
      />
      <StatPill
        to="/tasks"
        icon={<IconClipboardList size={14} />}
        value={activeTasks}
        label="Active"
        ariaLabel="Active tasks"
        loading={tasks.isLoading}
      />
      {canSeeOperatorStats && (
        <StatPill
          to="/alerts"
          icon={<IconBell size={14} />}
          value={openAlerts}
          label="Alerts"
          ariaLabel="Open alerts"
          title="Open alerts, including device problems (robots, doors, lifts, gateways)."
          loading={alertSummary.isLoading}
          tone={!alertSummary.isLoading && openAlerts !== null && openAlerts > 0 ? 'error' : 'default'}
        />
      )}
      {canSeeOperatorStats && (
        <StatPill
          to="/support"
          icon={<IconLifeBuoy size={14} />}
          value={unresolvedIssues}
          label="Issues"
          ariaLabel="Unresolved issues"
          title="Open and in-progress issues. Device problems are counted under Alerts."
          loading={issueSummary.isLoading}
          tone={!issueSummary.isLoading && unresolvedIssues > 0 ? 'warning' : 'default'}
        />
      )}
    </div>
  );
}
